import { throttle } from "lodash";
import { storeToRefs } from "pinia";
import { Ref, computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { useStekManager } from "@/composables/stek-manager";
import { Artefact } from "@/lib/artefacts";
import { getDescriptionArtefact } from "@/lib/artefacts/description";
import { syncChannel } from "@/lib/broadcast-channels";
import { api, type types } from "@/lib/openapi";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { defineEntityStore } from "@/stores/helpers";
import { useReleaseStore } from "@/stores/release-store";
import { useSyncStore } from "@/stores/sync-store";

import {
    FILE_SYNC_THROTTLE_MS,
    discardDraftsAfterSync,
    useDraftManager,
} from "./draft-manager";
import { useEditManager } from "./edit-manager";
import { useEntityNameManager } from "./entity-name-manager";
import { useOrganizationManager } from "./organization-manager";

export type Domain = types.Domain;

// Uses the current route to determine what the 'domain' should be and fetches
// the domain description for you.
export const useDomainStore = defineEntityStore("domain", () => {
    const router = useRouter();
    const { organization } = useOrganizationManager();
    const { stek } = useStekManager();
    const syncStore = useSyncStore();
    const entityId = computed(() => domain.value?.identity);
    const description: Ref<Artefact<"text/markdown"> | undefined> = ref();
    const descriptionDraft = useDraftManager({
        entityId,
        entityType: EntityType.DOMAIN,
        attribute: "description",
        artefact: description,
    });
    discardDraftsAfterSync(entityId, [descriptionDraft], EntityType.DOMAIN);

    const { domain: domainInfo } = useCurrentEntityManager();
    const releaseStore = useReleaseStore();
    const revisionQuery = computed(() =>
        releaseStore.activeRevision
            ? new URLSearchParams({ revision: releaseStore.activeRevision })
            : undefined
    );

    const isFetching = ref(false);
    const isFetchingDescription = ref(false);

    const domain: Ref<Domain | undefined> = ref(undefined);
    const {
        isModified: isNameModified,
        reset: resetName,
        persist: persistName,
    } = useEntityNameManager(domain);
    const controller = ref(new AbortController());

    async function fetchDomain(force = false) {
        const domainId = domainInfo.value?.identity;

        // In case the same domain is selected, return early.
        if (!force && domain.value && domain.value.identity === domainId) {
            return;
        }

        // Abort any previously running requests and create a new controller
        controller.value.abort();
        controller.value = new AbortController();

        if (!domainId) {
            description.value = undefined;
            domain.value = undefined;
            return;
        }

        isFetching.value = true;
        description.value = undefined;
        domain.value = undefined;

        try {
            const { response, data } =
                await api.startDomainDomainMgtGetDomainStartPost({
                    body: {
                        domain_id: `${domainId}`,
                        revision: releaseStore.activeRevision ?? undefined,
                    },
                    signal: controller.value.signal,
                });

            if (!response.ok) {
                if (response.status === 404) {
                    router.replace({
                        name: "not-found",
                        params: {
                            orgName: organization.value!.name,
                            stekName: stek.value!.name,
                        },
                        query: { type: "domain", id: domainId },
                    });
                    return;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            domain.value = data;

            // Fetch description while we're still in the try block before setting isFetching=false
            if (domain.value) {
                await fetchDescription(domainId);
            }
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                // Request was aborted by the AbortController.
                return;
            } else {
                throw e;
            }
        } finally {
            isFetching.value = false;
        }
    }
    watch(
        () => domainInfo.value?.identity,
        () => fetchDomain(),
        { immediate: true }
    );
    watch(
        () => releaseStore.activeRevision,
        () => {
            if (domain.value) fetchDomain(true);
        }
    );

    const fetchDescription = async (currentDomainId?: string | undefined) => {
        if (!description.value && domain.value) {
            // Store the current domain ID to detect changes during async operation
            isFetchingDescription.value = true;
            try {
                const descriptionResult = await getDescriptionArtefact(
                    domain.value,
                    "domainDescription",
                    controller.value.signal,
                    revisionQuery.value
                );

                // Check if domain has changed during the async operation
                if (
                    !domain.value ||
                    domain.value.identity !== currentDomainId
                ) {
                    console.debug(
                        "Domain changed during description fetch, aborting description update"
                    );
                    return;
                }

                description.value = descriptionResult;
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    // Request was aborted by the AbortController.
                    return;
                } else {
                    throw e;
                }
            } finally {
                isFetchingDescription.value = false;
            }
        }
    };

    const isModified = computed(() => {
        if (isFetching.value) {
            return false;
        }
        return Boolean(isNameModified.value || description.value?.isModified);
    });

    const queueSave = async () => {
        if (!isModified.value || !domain.value) {
            return;
        }

        const updateContent: types.StartDomainDomainMgtUpdateDomainStartPostData =
            {
                body: {
                    domain_id: domain.value.identity,
                },
                url: "/domain/domain_mgt/update_domain/start",
            };
        if (isNameModified.value) {
            updateContent.body.name = domain.value.name;
        }
        if (description.value?.isModified) {
            updateContent.body.description = description.value.blob;
        }

        if (
            updateContent.body.domain_id &&
            Object.keys(updateContent.body).length === 1
        ) {
            // Only domain_id is set, no need to update.
            return;
        }

        syncStore.queueSync({
            entityType: EntityType.DOMAIN,
            args: [updateContent],
        });

        if (description.value?.isModified) {
            description.value.persist();
        }
        if (isNameModified.value) {
            persistName();
        }
    };

    const reset = async () => {
        await description.value?.resetContent();
        resetName();
    };

    const { isEditing } = useEditManager();
    const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);
    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) {
            throttledSave();
        }
    });

    syncChannel.addEventListener(
        "message",
        ({ data: { message, identity } }) => {
            if (
                message === "synced" &&
                identity.organizationId === organization.value?.identity &&
                identity.entityType === EntityType.DOMAIN &&
                identity.entityId === domain.value?.identity
            ) {
                fetchDomain(true);
            }
        }
    );

    return {
        domain,
        description,
        isFetching: computed(
            () => isFetching.value || isFetchingDescription.value
        ),
        isModified,
        reset,
    };
});

export const useDomainManager = () => storeToRefs(useDomainStore());
